import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Button from "./Button";
import RegistrationModal from "./RegistrationModal";
import ProductRegistrationForm from "./ProductRegistrationForm";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const ReceiptImageModal = ({ url, onClose }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[199]"
        style={{ width: "100vw", height: "100vh", backdropFilter: "blur(4px)" }}
        onClick={onClose}
      />
      <div
        className="fixed inset-0 z-[200] flex items-center justify-center p-4"
        style={{ width: "100vw", height: "100vh" }}
        onClick={onClose}
      >
      <div
        className="relative w-full h-full flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close receipt"
          className="absolute top-0 right-0 bg-white rounded-full w-8 h-8 flex items-center justify-center text-gray-600 hover:text-black cursor-pointer"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        {!loaded && (
          <div
            className="w-full rounded-lg receipt-modal-in"
            style={{ maxHeight: "80vh", height: "60vh", background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s ease-in-out infinite" }}
          />
        )}
        <img
          src={url}
          alt="Receipt"
          className="w-full rounded-lg object-contain receipt-modal-in"
          style={{ maxHeight: "80vh", display: loaded ? "block" : "none" }}
          onLoad={() => setLoaded(true)}
        />
      </div>
    </div>
    </>,
    document.body
  );
};

const ReceiptRow = ({ receipt }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const count = receipt.products.length;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        aria-expanded={isOpen}
        className="w-full text-left bg-black hover:bg-gray-900 transition-colors cursor-pointer"
      >
        <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 px-5 py-4">
          <span className="font-medium text-white truncate">
            Registration #{receipt.id}
          </span>
          <span className="text-sm text-gray-300 whitespace-nowrap">
            {count} {count === 1 ? "product" : "products"}
          </span>
          <span className="text-sm text-gray-300 whitespace-nowrap">
            {formatDate(receipt.purchaseDate)}
          </span>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {receiptModalOpen && (
        <ReceiptImageModal url={receipt.receiptUrl} onClose={() => setReceiptModalOpen(false)} />
      )}

      {isOpen && (
        <div className="border-t border-gray-100 bg-gray-50">
          <div className="px-5 pt-4 pb-2 flex justify-end">
            <button
              type="button"
              onClick={() => setReceiptModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-black text-white hover:bg-gray-800 transition-colors cursor-pointer"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              View Receipt
            </button>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 text-xs uppercase tracking-wide border-b border-gray-200">
                <th className="px-5 py-2 font-medium w-1/2">Product</th>
                <th className="px-5 py-2 font-medium w-1/4">Serial #</th>
                <th className="px-5 py-2 font-medium w-1/4">Warranty Expires</th>
                <th className="px-5 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {receipt.products.map((p) => (
                <tr key={p.id} className="border-b border-gray-100 last:border-none bg-white">
                  <td className="px-5 py-3 text-gray-800">{p.product?.name ?? "—"}</td>
                  <td className="px-5 py-3 text-gray-600 font-mono">{p.serialNumber}</td>
                  <td className="px-5 py-3 text-gray-600">{formatDate(p.warrantyExpiration)}</td>
                  <td className="px-5 py-3">
                    {p.checked
                      ? <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800">Active</span>
                      : <span className="px-2 py-1 rounded text-xs font-semibold bg-yellow-100 text-yellow-800">Pending</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ProductWarranties = () => {
  const [regModalOpen, setRegModalOpen] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("regModal") === "true" || params.has("warranty");
  });

  const [showBanner, setShowBanner] = useState(false);
  const bannerTimerRef = useRef(null);

  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const fetchRegistrations = useCallback(() => {
    setLoading(true);
    setFetchError(null);
    axios
      .get("https://api.blizzardpro.com/registrations")
      .then((res) => setReceipts(res.data))
      .catch((err) => {
        console.error("Failed to load registrations", err);
        setFetchError("Failed to load registrations.");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  useEffect(() => () => clearTimeout(bannerTimerRef.current), []);

  const handleSuccess = useCallback(() => {
    setRegModalOpen(false);
    setShowBanner(true);
    clearTimeout(bannerTimerRef.current);
    bannerTimerRef.current = setTimeout(() => setShowBanner(false), 5000);
    fetchRegistrations();
  }, [fetchRegistrations]);

  return (
    <div>
      {showBanner && (
        <div className="fixed top-0 left-0 right-0 z-[100] flex items-center justify-between gap-4 bg-green-600 text-white px-6 py-4 shadow-lg">
          <span className="font-medium">Product registered successfully. Thank you!</span>
          <button
            type="button"
            onClick={() => setShowBanner(false)}
            aria-label="Dismiss notification"
            className="text-white/80 hover:text-white transition-colors cursor-pointer"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      {/* Register / extend warranty */}
      <div className="fade-up fade-up-delay-1">
        <h2 className="text-2xl font-bold">Extend a Product's Warranty</h2>
        <p>Fill out a product registration form to extend eligible product warranties by 1-year</p>
        <Button title="Register Product" onClick={() => setRegModalOpen(true)} />
      </div>

      {/* Receipts list */}
      <div className="mt-10 fade-up fade-up-delay-2">
        <h2 className="text-2xl font-bold mb-1">Your Receipts</h2>
        <p className="text-gray-500 mb-6">Registered receipts and their covered products</p>

        {loading ? (
          <p className="text-gray-400">Loading receipts…</p>
        ) : fetchError ? (
          <p className="text-red-500">{fetchError}</p>
        ) : receipts.length === 0 ? (
          <p className="text-gray-400">No receipts found.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {receipts.map((receipt) => (
              <ReceiptRow key={receipt.id} receipt={receipt} />
            ))}
          </div>
        )}
      </div>

      <RegistrationModal isOpen={regModalOpen} onClose={() => setRegModalOpen(false)}>
        <ProductRegistrationForm onSuccess={handleSuccess} />
      </RegistrationModal>
    </div>
  );
};

export default ProductWarranties;
